import { useEffect, useMemo, useState } from 'react'
import {
  ScanLine, AlertTriangle, Check, Store, Plus, Loader2, X, CircleAlert, FileText, Layers,
} from 'lucide-react'
import { ocrToForm, pickPatch } from '../../lib/expenseOcr.js'
import { fmtMoney } from '../../lib/expense.js'

/**
 * Review-before-apply for a scanned attachment.
 *
 * Nothing is written until the user presses ใช้ข้อมูลนี้. Every group can be
 * unticked, and anything the model flagged for review is marked so the eye goes
 * there first — the flags proved well aimed in testing (they landed on the fields
 * that genuinely could not be read), so they are worth surfacing rather than
 * burying.
 *
 * TWO THINGS THIS DIALOG EXISTS TO GET RIGHT, both of which staff hit daily:
 *  1. ONE FILE, SEVERAL INVOICES. The endpoint returns every document it found; they
 *     are all ticked by default and merged into one record, each line keeping its own
 *     อ้างอิง. Untick the ones that belong somewhere else.
 *  2. A SECOND FILE ON A RECORD THAT ALREADY HAS LINES. The default is เพิ่ม — new
 *     lines are APPENDED and any line the record already holds is skipped, so reading
 *     the same document twice changes nothing. แทนที่ is a deliberate choice, not
 *     what happens by accident.
 */
export default function OcrReviewDialog({ result, form, billing: existingBilling = [], onApply, onCancel }) {
  const { secs } = result || {}

  // The endpoint answers with `documents[]`; `extracted` is its first entry, kept for
  // the single-document shape this dialog was originally written against.
  const docs = useMemo(() => {
    if (Array.isArray(result?.documents) && result.documents.length) return result.documents
    return result?.extracted ? [result.extracted] : []
  }, [result])
  const matches = useMemo(() => {
    if (Array.isArray(result?.seller_matches) && result.seller_matches.length) return result.seller_matches
    return result?.seller_match ? [result.seller_match] : []
  }, [result])

  // Which of the documents in this file belong in THIS expense. All of them, until
  // the user says otherwise — a stack is scanned together because it is paid together.
  const [picked, setPicked] = useState(() => docs.map(() => true))
  const selected = useMemo(() => docs.filter((_, i) => picked[i]), [docs, picked])
  const headIndex = picked.findIndex(Boolean)
  const extracted = selected[0] || docs[0]
  const match = matches[headIndex >= 0 ? headIndex : 0]

  // What happens to lines the record already holds. Append is the default and the
  // safe one; replace has to be chosen.
  const hasExisting = existingBilling.length > 0
  const [mode, setMode] = useState('append')

  const mapped = useMemo(
    () => ocrToForm(selected, {
      mode,
      existingBilling,
      existingDiscount: form?.discount,
      existingVatAdd: form?.VAT_add,
      existingVatInclude: form?.VAT_include,
      currentWhtPercent: form?.WHT_percent,
      currentWhtType: form?.WHT_type,
    }),
    [selected, mode, existingBilling, form?.discount, form?.VAT_add, form?.VAT_include, form?.WHT_percent, form?.WHT_type],
  )
  const { patch, billing, warnings, fields } = mapped

  // Adding a document to a record that already has lines means adding money to it,
  // not re-describing it — so the header groups start unticked and the ones that
  // carry the new lines start ticked.
  const [chosen, setChosen] = useState(() =>
    Object.fromEntries(fields.map((f) => [
      f.key,
      !f.disabled && (!hasExisting || f.key === 'billing' || f.key === 'amount'),
    ])),
  )
  // Which existing vendor to link. `null` = keep as an unlinked snapshot.
  const [sellerId, setSellerId] = useState(
    () => (match?.status === 'unique' ? match.candidates[0]?.id ?? null : null),
  )

  // Switching to แทนที่ re-enables the รายการซื้อ group — under replace every line
  // counts as new again — so it comes back ticked instead of silently staying off.
  // Only a change in the group's availability re-ticks it; an unticked-by-hand group
  // stays unticked.
  const billingOff = !!fields.find((f) => f.key === 'billing')?.disabled
  useEffect(() => {
    setChosen((c) => (billingOff || c.billing ? c : { ...c, billing: true }))
  }, [billingOff])

  // The head document decides which vendor is on offer; changing the document
  // selection must not leave a link to the previous head's vendor behind.
  useEffect(() => {
    setSellerId(match?.status === 'unique' ? match.candidates[0]?.id ?? null : null)
  }, [match])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  const toggle = (k) => setChosen((c) => ({ ...c, [k]: !c[k] }))
  const toggleDoc = (i) => setPicked((p) => p.map((v, idx) => (idx === i ? !v : v)))
  const anyChosen = Object.entries(chosen).some(([k, v]) => v && !fields.find((f) => f.key === k)?.disabled)

  function apply() {
    const next = pickPatch(patch, fields, chosen)
    // seller_id only rides along if the seller group was actually applied.
    if (chosen.seller && sellerId) next.seller_id = sellerId
    else if (chosen.seller) next.seller_id = 0
    // An empty row list is never handed over: in replace mode it would clear the
    // record's lines, which is not what "nothing new to add" means.
    const rows = chosen.billing && billing.length ? billing : null
    onApply({ patch: next, billing: rows, mode })
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={onCancel} />
      <div className="relative w-full max-w-[560px] max-h-[88vh] flex flex-col bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 animate-ss-pop-in">

        <div className="flex items-start gap-3 p-5 pb-4 border-b border-slate-100">
          <span className="w-9 h-9 rounded-full flex items-center justify-center ring-1 flex-none text-brand-600 bg-brand-50 ring-brand-100">
            <ScanLine size={18} strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[15.5px] font-semibold text-slate-900">ตรวจสอบข้อมูลที่อ่านได้</div>
            <div className="text-[12.5px] text-slate-500 mt-0.5">
              {docs.length > 1
                ? `พบ ${docs.length} เอกสารในไฟล์นี้ · เลือกไว้ ${selected.length}`
                : `${extracted?.doc_type || 'เอกสาร'}${extracted?.doc_number ? ` · เลขที่ ${extracted.doc_number}` : ''}`}
              {secs ? ` · ${secs}s` : ''}
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 -m-1 text-slate-400 hover:text-slate-600 rounded-lg" aria-label="ปิด">
            <X size={17} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 pt-4 space-y-3">

          {/* One file, several documents. Staff scan a stack; the ones paid together
              belong in one record, and each line keeps its own อ้างอิง so they stay
              separable afterwards. */}
          {docs.length > 1 && (
            <div className="rounded-xl border border-brand-200 bg-brand-50/40 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-brand-700 mb-2">
                <Layers size={13} /> เอกสารในไฟล์นี้ — เลือกที่จะรวมเข้ารายการนี้
              </div>
              <div className="space-y-1.5">
                {docs.map((d, i) => {
                  const on = !!picked[i]
                  const isHead = i === headIndex
                  return (
                    <label
                      key={i}
                      className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 ring-1 transition cursor-pointer ${
                        on ? 'bg-white ring-brand-200' : 'bg-white/50 ring-slate-200 opacity-60'
                      }`}
                    >
                      <input
                        type="checkbox" checked={on} onChange={() => toggleDoc(i)}
                        className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 flex-none"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <FileText size={12} className="text-slate-400 flex-none" />
                          <span className="text-[12.5px] text-slate-700 truncate">
                            {d?.doc_type || 'เอกสาร'}{d?.doc_number ? ` · ${d.doc_number}` : ''}
                          </span>
                          {isHead && (
                            <span className="rounded-full bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[10.5px] font-medium">
                              ใช้เป็นหัวรายการ
                            </span>
                          )}
                        </div>
                        <div className="text-[11.5px] text-slate-400 truncate mt-0.5">
                          {[d?.seller?.seller_name, d?.date_buy].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      <span className="text-[12.5px] text-slate-600 tabular-nums flex-none">
                        {fmtMoney(d?.amount?.total_price_afterVAT)}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          {/* The record already has lines. Appending is the default — replacing them
              is a decision, not a side effect of pressing อ่านอัตโนมัติ on a second file. */}
          {hasExisting && (
            <div className="rounded-xl border border-slate-200 px-3 py-2.5">
              <div className="text-[12px] font-medium text-slate-500 mb-2">
                รายการนี้มีรายการซื้ออยู่แล้ว {existingBilling.length} รายการ
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { k: 'append', label: 'เพิ่มเข้าไป', hint: 'ข้ามรายการที่ซ้ำ' },
                  { k: 'replace', label: 'แทนที่ทั้งหมด', hint: 'ลบรายการเดิมทิ้ง' },
                ].map((o) => (
                  <button
                    key={o.k} type="button" onClick={() => setMode(o.k)}
                    className={`rounded-lg px-2.5 py-2 text-left ring-1 transition ${
                      mode === o.k
                        ? (o.k === 'replace' ? 'bg-rose-50 ring-rose-200' : 'bg-emerald-50 ring-emerald-200')
                        : 'bg-white ring-slate-200 hover:ring-slate-300'
                    }`}
                  >
                    <div className="text-[13px] font-medium text-slate-700">{o.label}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{o.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!selected.length && (
            <div className="flex items-start gap-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5 text-[12.5px] text-slate-500">
              ยังไม่ได้เลือกเอกสาร — เลือกอย่างน้อยหนึ่งฉบับ
            </div>
          )}

          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 rounded-xl bg-amber-50 ring-1 ring-amber-200/70 px-3 py-2.5">
              <AlertTriangle size={15} className="text-amber-600 mt-0.5 flex-none" />
              <div className="text-[12.5px] text-amber-900 leading-relaxed break-words">{w}</div>
            </div>
          ))}

          {fields.map((f) => (
            <label
              key={f.key}
              className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 transition cursor-pointer ${
                f.disabled
                  ? 'border-slate-100 bg-slate-50/60 cursor-not-allowed opacity-60'
                  : chosen[f.key]
                    ? 'border-brand-200 bg-brand-50/40'
                    : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={!!chosen[f.key] && !f.disabled}
                disabled={f.disabled}
                onChange={() => toggle(f.key)}
                className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 flex-none"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[12px] font-medium text-slate-500">{f.label}</span>
                  {f.flagged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10.5px] font-medium">
                      <CircleAlert size={10} strokeWidth={2.5} /> ควรตรวจสอบ
                    </span>
                  )}
                </div>
                <div className="text-[14px] text-slate-800 break-words mt-0.5">{f.value}</div>
                {f.hint && <div className="text-[11.5px] text-slate-400 mt-0.5 break-words">{f.hint}</div>}
              </div>
            </label>
          ))}

          {/* Vendor linking. A Thai tax ID identifies a legal entity, not a branch,
              so a chain retailer legitimately matches several rows — offer the
              choice rather than guessing. */}
          {chosen.seller && !!patch.seller_taxid && (
            <div className="rounded-xl border border-slate-200 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 mb-2">
                <Store size={13} /> เชื่อมกับบริษัทในระบบ
              </div>

              {match?.status === 'new' && (
                <div className="text-[12.5px] text-slate-500">
                  ไม่พบบริษัทนี้ในระบบ — จะบันทึกเป็นข้อมูลผู้ขายของรายการนี้
                  <span className="block text-[11.5px] text-slate-400 mt-0.5">
                    กด “เพิ่มเข้าระบบ” ในหน้าหลักหลังจากนี้ เพื่อใช้ซ้ำครั้งต่อไป
                  </span>
                </div>
              )}
              {match?.status === 'invalid_taxid' && (
                <div className="text-[12.5px] text-amber-700">เลขภาษีไม่ผ่านการตรวจสอบ จึงไม่ได้ค้นหาในระบบ</div>
              )}
              {match?.status === 'lookup_failed' && (
                <div className="text-[12.5px] text-slate-500">ค้นหาบริษัทไม่สำเร็จ — เลือกเองในหน้าหลักได้</div>
              )}

              {!!match?.candidates?.length && (
                <div className="space-y-1.5">
                  {match.status === 'ambiguous' && (
                    <div className="text-[11.5px] text-amber-700 mb-1.5">
                      เลขภาษีนี้ตรงกับ {match.candidates.length} สาขา
                      {extracted?.seller?.seller_branch ? ` — เอกสารระบุสาขาที่ ${extracted.seller.seller_branch}` : ''} เลือกสาขาที่ถูกต้อง
                    </div>
                  )}
                  {match.candidates.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSellerId(sellerId === c.id ? null : c.id)}
                      className={`w-full text-left rounded-lg px-2.5 py-2 transition ring-1 ${
                        sellerId === c.id
                          ? 'bg-emerald-50 ring-emerald-200'
                          : 'bg-white ring-slate-200 hover:ring-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {sellerId === c.id && <Check size={13} className="text-emerald-600 flex-none" strokeWidth={2.5} />}
                        <span className="text-[13px] text-slate-700 truncate flex-1">{c.name}</span>
                        <span className="text-[11px] text-slate-400 tabular-nums flex-none">#{c.id}</span>
                      </div>
                      {c.address && <div className="text-[11px] text-slate-400 truncate mt-0.5">{c.address}</div>}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSellerId(null)}
                    className={`w-full text-left rounded-lg px-2.5 py-2 transition ring-1 flex items-center gap-2 ${
                      sellerId === null ? 'bg-slate-100 ring-slate-300' : 'bg-white ring-slate-200 hover:ring-slate-300'
                    }`}
                  >
                    <Plus size={13} className="text-slate-400 flex-none" />
                    <span className="text-[12.5px] text-slate-600">ไม่เชื่อม — ใช้ข้อมูลจากเอกสารอย่างเดียว</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-100">
          <span className="text-[11.5px] text-slate-400">ยังไม่บันทึก — กดบันทึกอีกครั้งในหน้าหลัก</span>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="rounded-xl px-3.5 py-2 text-[13.5px] font-medium text-slate-600 hover:bg-slate-100 transition">
              ยกเลิก
            </button>
            <button
              onClick={apply}
              disabled={!anyChosen}
              className="rounded-xl px-4 py-2 text-[13.5px] font-medium bg-brand-600 hover:bg-brand-700 text-white transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ใช้ข้อมูลนี้
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Small inline button used on each attached file row. */
export function ScanButton({ busy, onClick, done }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title="อ่านข้อมูลจากเอกสารนี้อัตโนมัติ"
      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11.5px] font-medium transition flex-none disabled:opacity-60 ${
        done
          ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
          : 'border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100'
      }`}
    >
      {busy
        ? <><Loader2 size={11} className="animate-spin" /> กำลังอ่าน…</>
        : done
          ? <><Check size={11} strokeWidth={2.5} /> อ่านแล้ว</>
          : <><ScanLine size={11} strokeWidth={2.5} /> อ่านอัตโนมัติ</>}
    </button>
  )
}
